import { useCallback, useState } from "react";
import { useDropzone, Accept, FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";

const DEFAULT_ACCEPT: Accept = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
};

interface Props {
  onFiles: (files: File[]) => void;
  accept?: Accept;
  /** i18n key for the resting hint. Defaults to `assetPropertiesPanel.documents.dragHint`. */
  hintKey?: string;
  /** i18n key for the drag-active hint. Defaults to `assetPropertiesPanel.documents.dropHere`. */
  activeHintKey?: string;
  /** Whether to allow multiple files. Defaults to true. */
  multiple?: boolean;
  /** Whether the dropzone is disabled (e.g., during import). Defaults to false. */
  disabled?: boolean;
  /** Called when a dropped file is rejected (wrong type). The area also shows a message itself. */
  onRejected?: (rejections: FileRejection[]) => void;
}

export default function DocumentUploadArea({
  onFiles,
  accept = DEFAULT_ACCEPT,
  hintKey = "assetPropertiesPanel.documents.dragHint",
  activeHintKey = "assetPropertiesPanel.documents.dropHere",
  multiple = true,
  disabled = false,
  onRejected,
}: Props) {
  const { t } = useTranslation();
  const [rejected, setRejected] = useState(false);
  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      if (accepted.length > 0) {
        setRejected(false);
        onFiles(accepted);
      } else if (rejections && rejections.length > 0) {
        // A dropped file of the wrong type must not fail silently.
        setRejected(true);
        onRejected?.(rejections);
      }
    },
    [onFiles, onRejected],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
    disabled,
  });
  const inputProps = getInputProps();
  return (
    <div
      {...getRootProps()}
      className="rounded-md border-2 border-dashed p-6 text-center text-sm text-muted-foreground cursor-pointer"
      style={{
        borderColor: isDragActive ? "#0078d4" : undefined,
        backgroundColor: isDragActive ? "#deecf9" : undefined,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <input
        {...inputProps}
        onClick={(e) => {
          (e.target as HTMLInputElement).value = "";
          inputProps.onClick?.(e);
        }}
      />
      {isDragActive ? t(activeHintKey) : t(hintKey)}
      {rejected ? (
        <div style={{ marginTop: 6, color: "#b00020" }}>
          {t("documentUpload.invalidType", {
            defaultValue: "Unzulässiger Dateityp",
          })}
        </div>
      ) : null}
    </div>
  );
}
